import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { format } from 'date-fns'
import { TriangleAlert } from 'lucide-react'
import { use, useMemo } from 'react'
import { CompanySuspensionContext } from '@/components/company-suspension/context'
import { ManageBillingButton } from '@/components/shared/manage-billing-button'
import { H3, Muted } from '@/components/sv/typographies'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import type {
  CompanyPastDueInvoicesQuery,
  CompanyPastDueInvoicesQueryVariables,
} from '@/routes/billing/past-due-invoices/index.types.gen'

const INVOICES_QUERY = gql`
  query CompanyPastDueInvoices($idCompany: String!) {
    company(id: $idCompany) {
      id
      sevallaBilling @live {
        id
        pastDueInvoices {
          id
          status
          periodStart
          periodEnd
          tax
          total
          attempted
          amountDue
          dueDate
          createdAt
        }
      }
    }
  }
`

type Row = NonNullable<
  NonNullable<
    NonNullable<CompanyPastDueInvoicesQuery['company']>['sevallaBilling']
  >['pastDueInvoices']
>[number]

export const PastDueInvoices = () => {
  const { isSuspended } = use(CompanySuspensionContext)
  const { idCompany } = useIdCompany()

  const { data } = useSuspenseQuery<
    CompanyPastDueInvoicesQuery,
    CompanyPastDueInvoicesQueryVariables
  >(INVOICES_QUERY, {
    variables: { idCompany },
  })

  const items = useMemo(
    () => data?.company?.sevallaBilling?.pastDueInvoices ?? [],
    [data]
  )

  const columns: ColumnDef<Row>[] = useMemo(
    () => [
      {
        accessorKey: 'createdAt',
        header: () => <div className="pl-2">Invoice date</div>,
        cell: ({ row }) => (
          <Muted className="pl-4 py-2">
            {format(new Date(row.original.createdAt), 'MMM d, yyyy')}
          </Muted>
        ),
      },
      {
        accessorKey: 'periodStart',
        header: () => <div className="pl-2">Period</div>,
        cell: ({ row }) => (
          <Muted className="pl-4 py-2">
            {format(new Date(row.original.periodStart), 'MMM d, yyyy')} -{' '}
            {format(new Date(row.original.periodEnd), 'MMM d, yyyy')}
          </Muted>
        ),
      },
      {
        accessorKey: 'amountDue',
        header: () => <div className="text-right">Due amount</div>,
        cell: ({ row }) => {
          if (!row.original.amountDue) {
            return
          }
          const amountDue = row.original.amountDue / 100
          return (
            <div className="text-right font-bold pr-2">
              ${amountDue.toFixed(2)}
            </div>
          )
        },
      },
    ],
    []
  )

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  if (!items?.length) {
    return null
  }

  return (
    <Card className="border-destructive">
      <CardHeader>
        <CardTitle>
          <H3 className="flex items-center gap-2 text-destructive/80">
            <TriangleAlert className="size-4" /> Past due invoices
          </H3>
        </CardTitle>
        <CardDescription className="font-medium">
          {isSuspended
            ? `Your account is currently suspended due to unpaid ${items?.length > 1 ? 'invoices' : 'invoice'}. Please settle the outstanding balance to restore your services.`
            : `To prevent service interruption, please clear the overdue ${items?.length > 1 ? 'invoices' : 'invoice'}.`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4">
          <div className="rounded-md border">
            <Table className="table-fixed">
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="bg-muted/50 hover:bg-muted/50"
                  >
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        style={{ width: header.getSize() }}
                        key={header.id}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length
                  ? table.getRowModel().rows.map((row) => (
                      <TableRow key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            className="p-0 m-0"
                            key={cell.id}
                            style={{ maxWidth: cell.column.getSize() }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  : null}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <ManageBillingButton variant="destructive" />
      </CardFooter>
    </Card>
  )
}
